import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import { analyticsService } from '../../services/upgradeAnalyticsService';

/**
 * UpgradeAnalyticsDashboard Component
 * Admin dashboard for monitoring free-tier upgrade prompt engagement
 * Displays metrics, conversion rates, and user engagement data
 */
const UpgradeAnalyticsDashboard = () => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [engagementData, setEngagementData] = useState(null);
  const [dateRange, setDateRange] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch analytics data
  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch upgrade analytics
      const analyticsResult = await analyticsService.getUpgradeAnalytics(dateRange);
      if (analyticsResult.success) {
        setAnalyticsData(analyticsResult.data);
      } else {
        setError('Failed to fetch upgrade analytics');
      }

      // Fetch free-tier engagement metrics
      const engagementResult = await analyticsService.getFreeTierEngagement(dateRange);
      if (engagementResult.success) {
        setEngagementData(engagementResult.data);
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Icon name="AlertCircle" size={48} className="text-red-500 mx-auto mb-4" />
          <p className="text-red-500">{error}</p>
          <Button onClick={fetchAnalytics} className="mt-4">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-text-primary">Upgrade Analytics</h1>
            <p className="text-text-secondary mt-1">Monitor free-tier user engagement and upgrade prompt performance</p>
          </div>
          <div className="flex gap-2">
            {['7d', '30d', '90d', '1y'].map(range => (
              <Button
                key={range}
                variant={dateRange === range ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setDateRange(range)}
              >
                {range === '7d' ? 'Last 7 Days' : range === '30d' ? 'Last 30 Days' : range === '90d' ? 'Last 90 Days' : 'Last Year'}
              </Button>
            ))}
          </div>
        </div>
        {analyticsData && (
          <p className="text-sm text-text-secondary">
            Period: {analyticsData.startDate} to {analyticsData.endDate}
          </p>
        )}
      </div>

      {/* Primary Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Prompt Views */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-text-secondary">Prompt Views</h3>
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
              <Icon name="Eye" size={20} className="text-blue-500" />
            </div>
          </div>
          <p className="text-3xl font-bold text-text-primary mb-2">
            {analyticsData?.metrics?.totalPromptViews || 0}
          </p>
          <p className="text-sm text-text-secondary">
            {analyticsData?.summary?.promptViewsPerDay || '0'} per day
          </p>
        </div>

        {/* Upgrade Clicks */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-text-secondary">Upgrade Clicks</h3>
            <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
              <Icon name="ArrowUp" size={20} className="text-green-500" />
            </div>
          </div>
          <p className="text-3xl font-bold text-text-primary mb-2">
            {analyticsData?.metrics?.totalPromptClicks || 0}
          </p>
          <p className="text-sm text-text-secondary">
            {analyticsData?.summary?.promptClicksPerDay || '0'} per day
          </p>
        </div>

        {/* Click Through Rate */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-text-secondary">Click-Through Rate</h3>
            <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
              <Icon name="Percent" size={20} className="text-purple-500" />
            </div>
          </div>
          <p className="text-3xl font-bold text-text-primary mb-2">
            {analyticsData?.metrics?.clickThroughRate || 0}%
          </p>
          <p className="text-sm text-text-secondary">
            of viewers clicked upgrade
          </p>
        </div>

        {/* Dismissal Rate */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-text-secondary">Dismissal Rate</h3>
            <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
              <Icon name="X" size={20} className="text-red-500" />
            </div>
          </div>
          <p className="text-3xl font-bold text-text-primary mb-2">
            {analyticsData?.metrics?.dismissRate || 0}%
          </p>
          <p className="text-sm text-text-secondary">
            of viewers dismissed
          </p>
        </div>
      </div>

      {/* Secondary Metrics Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Conversion Summary */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
              <Icon name="CheckCircle" size={20} className="text-green-500" />
            </div>
            <h3 className="text-lg font-semibold text-text-primary">Conversion Summary</h3>
          </div>
          <p className="text-2xl font-bold text-green-600 mb-2">
            {analyticsData?.summary?.conversionSummary}
          </p>
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-sm text-text-secondary mb-2">Conversion Rate: <strong className="text-text-primary">{analyticsData?.metrics?.conversionRate}%</strong></p>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-green-500 h-2 rounded-full transition-all"
                style={{ width: `${analyticsData?.metrics?.conversionRate || 0}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Free-Tier Engagement */}
        {engagementData && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                <Icon name="Users" size={20} className="text-blue-500" />
              </div>
              <h3 className="text-lg font-semibold text-text-primary">Free-Tier Engagement</h3>
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-text-secondary mb-1">Total Free-Tier Users</p>
                <p className="text-2xl font-bold text-text-primary">{engagementData.totalFreeTierUsers}</p>
              </div>
              <div>
                <p className="text-sm text-text-secondary mb-1">Engaged Users</p>
                <p className="text-2xl font-bold text-text-primary">
                  {engagementData.engagedUsers} <span className="text-sm font-normal text-text-secondary">({engagementData.engagementRate}%)</span>
                </p>
              </div>
              <div>
                <p className="text-sm text-text-secondary mb-1">Users Who Viewed Upgrade</p>
                <p className="text-lg font-semibold text-text-primary">{engagementData.metrics.promptViews}</p>
              </div>
              <div>
                <p className="text-sm text-text-secondary mb-1">Users Who Clicked Upgrade</p>
                <p className="text-lg font-semibold text-green-600">{engagementData.conversion.upgradedUsers}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Detailed Metrics Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-text-primary">Detailed Metrics</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold text-text-secondary">Metric</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-text-secondary">Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm text-text-secondary">Total Upgrade Prompt Views</td>
                <td className="px-6 py-4 text-right text-sm font-semibold text-text-primary">
                  {analyticsData?.metrics?.totalPromptViews || 0}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm text-text-secondary">Total Upgrade Clicks</td>
                <td className="px-6 py-4 text-right text-sm font-semibold text-green-600">
                  {analyticsData?.metrics?.totalPromptClicks || 0}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm text-text-secondary">Total Dismissals</td>
                <td className="px-6 py-4 text-right text-sm font-semibold text-red-600">
                  {analyticsData?.metrics?.totalPromptDismisses || 0}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm text-text-secondary">Candidate Matches Viewed by Free Users</td>
                <td className="px-6 py-4 text-right text-sm font-semibold text-text-primary">
                  {analyticsData?.metrics?.totalCandidateViews || 0}
                </td>
              </tr>
              <tr className="hover:bg-gray-50 bg-blue-50">
                <td className="px-6 py-4 text-sm font-semibold text-text-primary">Click-Through Rate</td>
                <td className="px-6 py-4 text-right text-sm font-bold text-blue-600">
                  {analyticsData?.metrics?.clickThroughRate || 0}%
                </td>
              </tr>
              <tr className="hover:bg-gray-50 bg-red-50">
                <td className="px-6 py-4 text-sm font-semibold text-text-primary">Dismissal Rate</td>
                <td className="px-6 py-4 text-right text-sm font-bold text-red-600">
                  {analyticsData?.metrics?.dismissRate || 0}%
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer Info */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-text-secondary">
        <p>
          <strong>Note:</strong> Analytics are updated in real-time. Data shown reflects activity from the selected date range. 
          Click-through rate is calculated as (Upgrade Clicks ÷ Prompt Views) × 100. Engagement rate is calculated as (Engaged Users ÷ Total Free-Tier Users) × 100.
        </p>
      </div>
    </div>
  );
};

export default UpgradeAnalyticsDashboard;
